import React, { useState, useRef, useEffect, useCallback } from "react";

// Full-screen photo viewer with pinch-zoom, double-tap zoom, pan, and swipe navigation.
// Props: photos (array of urls), index, onClose, onIndex(newIndex)
export default function Lightbox({ photos, index, onClose, onIndex }) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [dir, setDir] = useState(0);   // -1 prev, +1 next (drives the slide direction)
  const imgWrapRef = useRef(null);

  // Navigate with a remembered direction so the incoming photo slides the right way.
  const go = useCallback((delta) => {
    setDir(delta);
    onIndex((index + delta + photos.length) % photos.length);
  }, [index, photos.length, onIndex]);

  // gesture refs
  const gesture = useRef({
    mode: null, startX: 0, startY: 0, startTx: 0, startTy: 0,
    startDist: 0, startScale: 1, midX: 0, midY: 0, lastTap: 0, swipeStartX: 0, moved: false,
  });

  const reset = useCallback(() => { setScale(1); setTx(0); setTy(0); }, []);
  useEffect(() => { reset(); }, [index, reset]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && scale === 1) go(1);
      if (e.key === "ArrowLeft" && scale === 1) go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose, scale]);

  const dist = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

  const clampPan = (nx, ny, s) => {
    const el = imgWrapRef.current;
    if (!el) return [nx, ny];
    const rect = el.getBoundingClientRect();
    const maxX = (rect.width  * (s - 1)) / 2;
    const maxY = (rect.height * (s - 1)) / 2;
    return [Math.max(-maxX, Math.min(maxX, nx)), Math.max(-maxY, Math.min(maxY, ny))];
  };

  const onTouchStart = (e) => {
    const g = gesture.current;
    g.moved = false;
    if (e.touches.length === 2) {
      g.mode = "pinch";
      g.startDist = dist(e.touches[0], e.touches[1]);
      g.startScale = scale;
      g.midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      g.midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      if (scale > 1) { g.mode = "pan"; g.startX = t.clientX; g.startY = t.clientY; g.startTx = tx; g.startTy = ty; }
      else { g.mode = "swipe"; g.swipeStartX = t.clientX; g.startY = t.clientY; }
    }
  };

  const onTouchMove = (e) => {
    const g = gesture.current;
    if (g.mode === "pinch" && e.touches.length === 2) {
      e.preventDefault();
      const d = dist(e.touches[0], e.touches[1]);
      let ns = Math.max(1, Math.min(5, g.startScale * (d / g.startDist)));
      setScale(ns);
      if (ns === 1) { setTx(0); setTy(0); }
      g.moved = true;
    } else if (g.mode === "pan" && e.touches.length === 1) {
      e.preventDefault();
      const t = e.touches[0];
      const [cx, cy] = clampPan(g.startTx + (t.clientX - g.startX), g.startTy + (t.clientY - g.startY), scale);
      setTx(cx); setTy(cy);
      g.moved = true;
    } else if (g.mode === "swipe" && e.touches.length === 1) {
      const t = e.touches[0];
      if (Math.abs(t.clientX - g.swipeStartX) > 8) g.moved = true;
    }
  };

  const onTouchEnd = (e) => {
    const g = gesture.current;
    if (g.mode === "swipe" && scale === 1) {
      const endX = (e.changedTouches[0] || {}).clientX ?? g.swipeStartX;
      const dx = endX - g.swipeStartX;
      if (Math.abs(dx) > 50) { go(dx < 0 ? 1 : -1); }
    }
    if (!g.moved && (g.mode === "swipe" || g.mode === "pan")) {
      const now = Date.now();
      if (now - g.lastTap < 300) { if (scale > 1) reset(); else setScale(2.5); g.lastTap = 0; }
      else { g.lastTap = now; }
    }
    g.mode = null;
  };

  const onWheel = (e) => {
    e.preventDefault();
    let ns = Math.max(1, Math.min(5, scale - e.deltaY * 0.002));
    setScale(ns);
    if (ns === 1) { setTx(0); setTy(0); }
  };

  const mouse = useRef({ down: false, x: 0, y: 0, tx: 0, ty: 0 });
  const onMouseDown = (e) => { if (scale <= 1) return; mouse.current = { down: true, x: e.clientX, y: e.clientY, tx, ty }; };
  const onMouseMove = (e) => {
    if (!mouse.current.down) return;
    const [cx, cy] = clampPan(mouse.current.tx + (e.clientX - mouse.current.x), mouse.current.ty + (e.clientY - mouse.current.y), scale);
    setTx(cx); setTy(cy);
  };
  const onMouseUp = () => { mouse.current.down = false; };

  const zoomed = scale > 1;
  const closeNow = (e) => { if (e) { e.stopPropagation(); e.preventDefault(); } onClose(); };

  return (
    <div
      onClick={() => { if (!zoomed) onClose(); }}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.94)", zIndex:400, display:"flex", alignItems:"center", justifyContent:"center", touchAction:"none", overflow:"hidden" }}
    >
      <style>{`
        @keyframes pmfLbInR { from { opacity:0; transform: translateX(34px);} to { opacity:1; transform: translateX(0);} }
        @keyframes pmfLbInL { from { opacity:0; transform: translateX(-34px);} to { opacity:1; transform: translateX(0);} }
      `}</style>

      {/* Close — lowered below the notch/status bar, large tap target, closes on tap or click */}
      <button onClick={closeNow} onTouchEnd={closeNow} aria-label="Close" style={closeBtn}>✕</button>

      {/* Counter */}
      <div style={{ position:"absolute", top:"calc(env(safe-area-inset-top, 0px) + 20px)", left:"calc(env(safe-area-inset-left, 0px) + 20px)", color:"rgba(255,255,255,0.7)", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, letterSpacing:"0.1em", zIndex:4 }}>
        {index + 1} / {photos.length}{zoomed ? `  ·  ${scale.toFixed(1)}×` : ""}
      </div>

      {photos.length > 1 && !zoomed && (
        <button onClick={(e) => { e.stopPropagation(); go(-1); }} style={arrow("left")}>‹</button>
      )}

      {/* Image */}
      <div
        ref={imgWrapRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onClick={(e) => e.stopPropagation()}
        style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", cursor: zoomed ? "grab" : "default" }}
      >
        {/* Keyed wrapper animates the incoming photo; the img keeps the zoom transform */}
        <div key={index} style={{ display:"flex", alignItems:"center", justifyContent:"center", animation: `${dir < 0 ? "pmfLbInL" : "pmfLbInR"} 0.28s cubic-bezier(0.22,0.61,0.36,1)` }}>
          <img
            src={photos[index]}
            alt=""
            draggable={false}
            style={{
              maxWidth:"92vw", maxHeight:"86vh", objectFit:"contain", borderRadius:6,
              transform:`translate(${tx}px, ${ty}px) scale(${scale})`,
              transition: mouse.current.down ? "none" : "transform 0.12s ease-out",
              userSelect:"none", WebkitUserDrag:"none",
            }}
          />
        </div>
      </div>

      {photos.length > 1 && !zoomed && (
        <button onClick={(e) => { e.stopPropagation(); go(1); }} style={arrow("right")}>›</button>
      )}

      {/* Hint */}
      <div style={{ position:"absolute", bottom:"calc(env(safe-area-inset-bottom, 0px) + 18px)", left:0, right:0, textAlign:"center", color:"rgba(255,255,255,0.45)", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.08em", zIndex:2 }}>
        {zoomed ? "drag to pan · double-tap to reset" : "pinch or double-tap to zoom · swipe to browse"}
      </div>
    </div>
  );
}

const closeBtn = {
  position:"absolute",
  top:"calc(env(safe-area-inset-top, 0px) + 14px)",
  right:"calc(env(safe-area-inset-right, 0px) + 16px)",
  background:"rgba(255,255,255,0.16)", color:"#fff", border:"none", borderRadius:"50%",
  width:46, height:46, fontSize:20, cursor:"pointer", zIndex:6,
  display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1,
  WebkitBackdropFilter:"blur(4px)", backdropFilter:"blur(4px)",
};
const arrow = (side) => ({
  position:"absolute", [side]:16, top:"50%", transform:"translateY(-50%)",
  background:"rgba(255,255,255,0.12)", color:"#fff", border:"none", borderRadius:"50%",
  width:52, height:52, fontSize:26, cursor:"pointer", zIndex:3,
});
